import { useState } from "preact/hooks";
import { Heart } from "lucide-react";
import { useToggleReviewLike } from "@/hooks/useReviewLike";
import { cn } from "@/lib/utils";
import { trackEvent } from "@/utils/tracking";
import { useReviewImpression } from "@/hooks/useReviewImpression";
import { queueReviewView } from "@/utils/reviewViews";
import type { Review } from "@/hooks/useReviews";
import StarRating from "./StarRating";

/**
 * Review text with the active filter's verbatim quote highlighted. Quotes are
 * extraction-validated substrings, so a plain indexOf suffices; if the text
 * was edited since extraction we silently skip the highlight.
 */
function highlightedComment(comment: string, quote?: string | null) {
  if (!quote) return comment;
  const start = comment.indexOf(quote);
  if (start === -1) return comment;

  return (
    <>
      {comment.slice(0, start)}
      {/* mark keeps the highlight semantics; bg-transparent kills the
          browser's default yellow — visually it's just bold. */}
      <mark className="tw:bg-transparent tw:font-medium tw:text-inherit">
        {quote}
      </mark>
      {comment.slice(start + quote.length)}
    </>
  );
}

// Full text always (Tor's call — no Show more); the ranking's length penalty
// keeps text walls away from the top instead.
function ReviewBody({
  comment,
  highlightQuote,
}: {
  comment: string;
  highlightQuote?: string | null;
}) {
  return (
    <p className="tw:text-base tw:font-normal tw:leading-relaxed tw:text-black">
      {highlightedComment(comment, highlightQuote)}
    </p>
  );
}

// Per-review "like" affordance. Initial state comes from the review payload
// (`liked`/`likes_count`); a tap optimistically flips it, fires the toggle
// endpoint, then reconciles with the server's authoritative response (and
// reverts on failure). Each tap is also tracked for the analytics funnel.
function ReviewHeart({
  review,
  surface,
}: {
  review: Review;
  surface: ReviewSurface;
}) {
  const [liked, setLiked] = useState(Boolean(review.liked));
  const [count, setCount] = useState(review.likes_count ?? 0);
  const toggleLike = useToggleReviewLike();

  const toggle = () => {
    if (review.id == null || toggleLike.isPending) return;

    const prevLiked = liked;
    const prevCount = count;
    const nextLiked = !prevLiked;

    // Optimistic flip — the heart reacts instantly.
    setLiked(nextLiked);
    setCount(prevCount + (nextLiked ? 1 : -1));

    trackEvent(nextLiked ? "reviews.review_liked" : "reviews.review_unliked", {
      surface,
      reviewId: review.id,
      reviewName: review.name,
      rating: review.rating,
    });

    toggleLike.mutate(review.id, {
      onSuccess: (result) => {
        setLiked(result.liked);
        setCount(result.likes_count);
      },
      onError: () => {
        setLiked(prevLiked);
        setCount(prevCount);
      },
    });
  };

  return (
    <div className="tw:flex tw:justify-end tw:me-2">
      <button
        type="button"
        onClick={toggle}
        aria-pressed={liked}
        aria-label="Like review"
        className="tw:flex tw:items-center tw:gap-1 tw:bg-transparent tw:border-none tw:p-0 tw:cursor-pointer tw:text-black"
      >
        {count > 0 ? <span className="tw:text-xs">{count}</span> : undefined}
        <Heart
          className="tw:size-4"
          strokeWidth={1}
          style={liked ? { fill: "#F6A7AB", color: "#F6A7AB" } : undefined}
        />
      </button>
    </div>
  );
}

/** "Customer since 2024 · 3 orders" — orders part hidden when there are none. */
function customerLine(review: Review) {
  const year = review.customer_since
    ? new Date(review.customer_since).getFullYear()
    : null;
  if (!year || Number.isNaN(year)) return null;

  const orders = review.orders_count ?? 0;
  if (orders === 0) return <>Customer since {year}</>;

  return (
    <>
      Customer since {year}
      {/* Round separator dot; the inline-flex parent centers it vertically,
          mx-2 gives 8px breathing room on each side. aria-hidden + the trailing
          space keep the count readable for screen readers (flex trims the space
          visually, so spacing stays symmetric). */}
      <span
        aria-hidden="true"
        className="tw:inline-block tw:size-[5px] tw:rounded-full tw:bg-current tw:mx-2 tw:shrink-0"
      />{" "}
      {orders} {orders === 1 ? "order" : "orders"}
    </>
  );
}

/** Analytics surface for like/chip events. "carousel" = the on-page widget. */
type ReviewSurface = "carousel" | "sheet";

interface ReviewItemProps {
  review: Review;
  /** Analytics surface for the like toggle. */
  surface: ReviewSurface;
  /** 0-based position in the rendered list; sent with the view impression. */
  index: number;
  /** Active filter chip key when this review is shown (null = unfiltered); sent with the view. */
  topic?: string | null;
  /** Verbatim filter quote to bold within the comment (sheet only). */
  highlightQuote?: string | null;
  /** Drop the bottom divider on the final item in a list. */
  last?: boolean;
}

/**
 * A single review card, shared verbatim by the sheet and the widget list so
 * both surfaces render identical cards: name + stars row, customer line,
 * full comment, and the like heart.
 */
export function ReviewItem({
  review,
  surface,
  index,
  topic,
  highlightQuote,
  last,
}: ReviewItemProps) {
  // Only the sheet's full list counts as a view — the on-page carousel preview
  // (top 3) is always rendered and isn't a deliberate "read", so it's excluded.
  const impressionRef = useReviewImpression({
    enabled: review.id != null && surface === "sheet",
    onImpression: () => {
      if (review.id != null) queueReviewView(review.id, index, topic ?? null);
    },
  });

  return (
    <div
      ref={impressionRef}
      className={cn(
        "tw:space-y-1 tw:relative tw:border-b-[0.5px] tw:border-[#c5cad2] tw:py-4",
        last ? "tw:border-b-0" : "",
      )}
    >
      <div className="tw:flex tw:flex-col tw:gap-2">
        <div>
          <h2 className="tw:text-base tw:font-medium tw:text-gray-900">
            {review.name}
          </h2>
          <div className="tw:flex tw:items-center tw:gap-2 tw:mt-1">
            <StarRating rating={review.rating || 5} size="sm" />
            {customerLine(review) ? (
              <span className="tw:inline-flex tw:items-center tw:text-xs tw:text-[#9d9d9d] tw:font-medium">
                {customerLine(review)}
              </span>
            ) : null}
          </div>
        </div>
        <ReviewBody comment={review.comment} highlightQuote={highlightQuote} />
        <ReviewHeart review={review} surface={surface} />
      </div>
    </div>
  );
}
